import { OrganizationType } from '../../types/organization.type';
import {
  OrganizationOnboardingFormState,
  requiresMwanFields,
} from '../../types/organization-onboarding.type';
import { isValidEmail, isValidSaudiPhone } from '../../helpers/validation.helper';
import { WASTE_TYPE_OTHER } from '../../constants/wasteTypes';

const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB

const ALLOWED_FILE_EXTENSIONS = new Set(['.pdf', '.png', '.jpg', '.jpeg']);

function fileErrorLabel(field: string): string {
  return `${field} must be a PDF or PNG/JPEG file (max 10 MB)`;
}

export function isAllowedOnboardingFile(file: File | null): boolean {
  if (!file) return false;
  if (file.size > MAX_FILE_BYTES) return false;
  const lower = file.name.toLowerCase();
  const dot = lower.lastIndexOf('.');
  if (dot < 0) return false;
  const ext = lower.slice(dot);
  if (!ALLOWED_FILE_EXTENSIONS.has(ext)) return false;
  const okMime =
    file.type === 'application/pdf' ||
    file.type === 'image/png' ||
    file.type === 'image/jpeg' ||
    file.type === '';
  return okMime;
}

function validateFileField(
  errors: Record<string, string>,
  key: keyof OrganizationOnboardingFormState,
  file: File | null,
  label: string
): void {
  if (!file) {
    errors[key as string] = `${label} is required`;
    return;
  }
  if (!isAllowedOnboardingFile(file)) {
    errors[key as string] = fileErrorLabel(label);
  }
}

export function validateOnboardingStep(
  step: number,
  form: OrganizationOnboardingFormState
): Record<string, string> {
  const errors: Record<string, string> = {};

  if (step === 0) {
    if (!form.name.trim()) errors.name = 'Organization name is required';
    if (!form.organization_type) errors.organization_type = 'Organization type is required';
    if (!form.city.trim()) errors.city = 'City is required';
    if (!form.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!isValidSaudiPhone(form.phone)) {
      errors.phone = 'Enter a valid Saudi phone (e.g. 05XXXXXXXX or +9665XXXXXXXX)';
    }
    if (form.contact_email.trim() && !isValidEmail(form.contact_email)) {
      errors.contact_email = 'Enter a valid email address';
    }
  }

  if (step === 1) {
    if (!form.cr_number.trim()) errors.cr_number = 'CR number is required';
    validateFileField(errors, 'cr_document', form.cr_document, 'CR document');
    if (!form.vat_number.trim()) errors.vat_number = 'VAT number is required';
    validateFileField(errors, 'vat_document', form.vat_document, 'VAT document');

    if (requiresMwanFields(form.organization_type as OrganizationType | '')) {
      validateFileField(errors, 'mwan_certificate_document', form.mwan_certificate_document, 'MWAN certificate');
      if (!form.mwan_license_number.trim()) {
        errors.mwan_license_number = 'MWAN licence number is required';
      }
    }

    validateFileField(errors, 'iban_certificate_document', form.iban_certificate_document, 'IBAN certificate');
    if (!form.iban.trim()) errors.iban = 'IBAN is required';
    if (!form.bank_name.trim()) errors.bank_name = 'Bank name is required';
    if (!form.bank_account_number.trim()) errors.bank_account_number = 'Bank account number is required';
  }

  if (step === 2) {
    if (!form.waste_types.length) errors.waste_types = 'Select at least one waste type';
    if (form.waste_types.includes(WASTE_TYPE_OTHER) && !form.other_waste_type.trim()) {
      errors.other_waste_type = 'Please describe the other waste type';
    }
    if (!form.google_maps_url.trim()) errors.google_maps_url = 'Google Maps link is required';
    if (!form.organization_address.trim()) errors.organization_address = 'Address is required';
    if (!form.national_address_short.trim()) {
      errors.national_address_short = 'National address short code is required';
    }
    validateFileField(errors, 'national_address_document', form.national_address_document, 'National address document');
    if (form.is_24_7 === null) errors.is_24_7 = 'Please indicate whether you operate 24/7';
    if (form.is_24_7 === false) {
      if (!form.operation_days.length) errors.operation_days = 'Select at least one operating day';
      if (!form.operation_start) errors.operation_start = 'Start time is required';
      if (!form.operation_end) errors.operation_end = 'End time is required';
      if (form.operation_start && form.operation_end) {
        const s = form.operation_start.getTime();
        const e = form.operation_end.getTime();
        if (e <= s) {
          errors.operation_end = 'End time must be after start time';
        }
      }
    }
  }

  if (step === 3) {
    if (!form.policies_acknowledged) {
      errors.policies_acknowledged = 'You must acknowledge the policies to continue';
    }
  }

  return errors;
}
